import { needsClientApproval } from "./approvalUtils";
import { fileNeedsAttentionForViewer } from "./perCollaboratorReviewRequests";
import { enrichPlanningPhotoCandidate, isPlanningProjectPhotoFile } from "./planningProjectPhotosModel";
import { sortDrawingsCanonical } from "./drawingOrderModel";

function safeStr(x) {
  return (x ?? "").toString();
}

/** Normalize `pmProjectFiles.reviewStatus` for comparisons (snake_case). */
export function normalizePmFileReviewStatus(x) {
  return safeStr(x).toLowerCase().trim().replace(/\s+/g, "_").replace(/-+/g, "_");
}

function normStatus(x) {
  return normalizePmFileReviewStatus(x);
}

function normToken(value) {
  return safeStr(value).toLowerCase().trim().replace(/[\s-]+/g, "_");
}

function textHasDrawingReviewIntent(value) {
  const text = normToken(value);
  if (!text) return false;
  return (
    text === "drawing" ||
    text === "drawings" ||
    text === "drawing_review" ||
    text === "drawings_review" ||
    text === "review_file" ||
    text === "file_review" ||
    text.includes("drawing")
  );
}

function hasExplicitDrawingReviewClassification(file = {}, enriched = null) {
  const row = enriched || file || {};
  return (
    textHasDrawingReviewIntent(file?.fileDomain) ||
    textHasDrawingReviewIntent(file?.reviewDomain) ||
    textHasDrawingReviewIntent(file?.domain) ||
    textHasDrawingReviewIntent(file?.manualLibraryType || file?.libraryTypeManual) ||
    textHasDrawingReviewIntent(row?.libraryType) ||
    textHasDrawingReviewIntent(row?.category) ||
    textHasDrawingReviewIntent(row?.subcategory) ||
    textHasDrawingReviewIntent(row?.deliverableType)
  );
}

/**
 * Drawings & review is a drawing/review file domain, not a generic project-file list.
 * Planning/project photos stay in Planning Photos unless they are explicitly classified
 * as drawing/review deliverables.
 */
export function isDrawingReviewDomainFile(file = {}) {
  if (safeStr(file?.status).toLowerCase() === "deleted") return false;
  const enriched = enrichPlanningPhotoCandidate(file);
  if (hasExplicitDrawingReviewClassification(file, enriched)) return true;
  if (isPlanningProjectPhotoFile(file, enriched)) return false;
  return false;
}

/** True only when client/admin formally requested a new revision (CL-337M). */
export function isRevisionsRequiredFile(file) {
  return normStatus(file?.reviewStatus) === "revisions_required";
}

/** Client-facing workflow status (UI only; derived from existing reviewStatus). */
export const WORKFLOW_STATUS = {
  NEEDS_REVIEW: "needs_review",
  IN_PROGRESS: "in_progress",
  APPROVED: "approved",
  CHANGES_REQUESTED: "changes_requested",
  /** `reviewStatus` `changes_submitted` — new revision in flight (CL-312). */
  REVISION_SUBMITTED: "revision_submitted",
  /** `reviewStatus` `under_review` — team/designer review before client decision (CL-337M). */
  UNDER_REVIEW: "under_review",
};

export const WORKFLOW_STATUS_LABEL = {
  needs_review: "Needs review",
  in_progress: "In progress",
  approved: "Approved",
  changes_requested: "Changes requested",
  revision_submitted: "Revision uploaded",
  under_review: "Under review",
};

/** Dot / badge tone for status pill */
export const WORKFLOW_STATUS_TONE = {
  needs_review: "bg-red-100 text-red-900 border-red-200",
  in_progress: "bg-blue-100 text-blue-900 border-blue-200",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-200",
  changes_requested: "bg-amber-100 text-amber-900 border-amber-200",
  revision_submitted: "bg-violet-100 text-violet-900 border-violet-200",
  under_review: "bg-slate-100 text-slate-800 border-slate-200",
};

/**
 * Maps a pmProjectFiles row to workflow status for badges (no schema change).
 */
export function getClientDrawingWorkflowStatus(file) {
  const f = file || {};
  const s = normStatus(f.reviewStatus);

  if (s === "approved" || s === "approved_as_noted") return WORKFLOW_STATUS.APPROVED;
  if (s === "pending_client_review") return WORKFLOW_STATUS.NEEDS_REVIEW;
  if (s === "under_review") return WORKFLOW_STATUS.UNDER_REVIEW;
  if (s === "changes_submitted") return WORKFLOW_STATUS.REVISION_SUBMITTED;
  if (
    s === "revisions_required" ||
    s === "returned" ||
    s === "rejected" ||
    s === "changes_requested"
  ) {
    return WORKFLOW_STATUS.CHANGES_REQUESTED;
  }
  if (s === "for_information" || s === "for_information_only" || s === "info_only") {
    return WORKFLOW_STATUS.APPROVED;
  }
  if (
    s === "closed" ||
    s === "no_action" ||
    s === "cancelled" ||
    s === "canceled" ||
    s === "on_hold" ||
    s === "draft"
  ) {
    return WORKFLOW_STATUS.IN_PROGRESS;
  }
  if (s === "in_review" || s === "submitted" || s === "resubmitted") {
    return WORKFLOW_STATUS.IN_PROGRESS;
  }
  if (!s) return WORKFLOW_STATUS.IN_PROGRESS;
  if (needsClientApproval(f)) return WORKFLOW_STATUS.NEEDS_REVIEW;
  return WORKFLOW_STATUS.IN_PROGRESS;
}

/**
 * Client portal / dashboard waiting queues (CL-LIVE-002).
 * Requires viewerContext for client attention. Without a viewer, returns false
 * (never invent attention from legacy pending_client_review alone).
 */
export function needsDrawingAttention(file, viewerContext = null) {
  if (!viewerContext) return false;
  return fileNeedsAttentionForViewer(file, viewerContext);
}

/** Contributor list: per-collaborator review request or revision upload (CL-385D). */
export function needsContributorDrawingAttention(file, viewerContext = null) {
  if (viewerContext && fileNeedsAttentionForViewer(file, viewerContext)) return true;
  if (isRevisionsRequiredFile(file)) return true;
  if (!viewerContext) {
    const w = getClientDrawingWorkflowStatus(file);
    if (w === WORKFLOW_STATUS.NEEDS_REVIEW) return true;
  }
  return false;
}

/**
 * Drawings & review tab: needs attention vs. reviewed (split by workflow status).
 * — needsReview: needs_review or changes_requested (see needsDrawingAttention)
 * — reviewedInProgress: in_progress
 * — reviewedApproved: approved (incl. approved_as_noted / for_information mapped to approved)
 */
/**
 * @param {string} [listAudience] `"client"` | `"admin"` | `"contributor"` — same file may sort into different columns (CL-312).
 * @param {object|null} [viewerContext]
 * @param {object|null} [projectOrOrderConfig] — optional `drawingOrderMode` / `drawingFileOrder` (CL-DRAW-ORDER-001)
 */
export function groupClientDrawingFilesForListForAudience(
  files,
  listAudience = "client",
  viewerContext = null,
  projectOrOrderConfig = null
) {
  const list = sortDrawingsCanonical(
    (Array.isArray(files) ? files : []).filter((f) => isDrawingReviewDomainFile(f)),
    projectOrOrderConfig,
    { includeExcluded: true }
  );
  const needsReview = [];
  const reviewedInProgress = [];
  const reviewedApproved = [];
  const aud = safeStr(listAudience).toLowerCase() || "client";
  const isContributor = aud === "contributor";
  const isClient = aud === "client";
  list.forEach((f) => {
    const w = getClientDrawingWorkflowStatus(f);
    const attention = isContributor
      ? needsContributorDrawingAttention(f, viewerContext)
      : needsDrawingAttention(f, viewerContext);
    if (w === WORKFLOW_STATUS.REVISION_SUBMITTED) {
      reviewedInProgress.push(f);
      return;
    }
    if (attention) {
      needsReview.push(f);
      return;
    }
    if (w === WORKFLOW_STATUS.APPROVED) {
      reviewedApproved.push(f);
      return;
    }
    // CL-LIVE-002: client untouched / shared-neutral drawings must not sit under "With the team."
    if (isClient) {
      const s = normStatus(f?.reviewStatus);
      const teamActive =
        w === WORKFLOW_STATUS.UNDER_REVIEW
        || w === WORKFLOW_STATUS.CHANGES_REQUESTED
        || s === "under_review"
        || s === "in_review"
        || s === "submitted"
        || s === "resubmitted"
        || s === "changes_submitted";
      if (teamActive) reviewedInProgress.push(f);
      else reviewedApproved.push(f);
      return;
    }
    reviewedInProgress.push(f);
  });
  return {
    needsReview: sortDrawingsCanonical(needsReview, projectOrOrderConfig, { includeExcluded: true }),
    reviewedInProgress: sortDrawingsCanonical(reviewedInProgress, projectOrOrderConfig, { includeExcluded: true }),
    reviewedApproved: sortDrawingsCanonical(reviewedApproved, projectOrOrderConfig, { includeExcluded: true }),
    all: list,
  };
}

/** @deprecated Prefer `groupClientDrawingFilesForListForAudience` with explicit audience. */
export function groupClientDrawingFilesForList(files) {
  return groupClientDrawingFilesForListForAudience(files, "client");
}

/* --- Planning tab (deliverables) compatibility: phase-based UI still uses bucket keys --- */

export const CLIENT_DRAWING_GROUP_ORDER = [
  "needs_review",
  "under_review",
  "changes_requested",
  "reviewed_waiting",
  "approved",
  "for_record",
  "other",
];

export const CLIENT_DRAWING_GROUP_LABEL = {
  needs_review: "Needs Review",
  under_review: "Under Review",
  changes_requested: "Changes Requested",
  reviewed_waiting: "Reviewed — Waiting on Team",
  approved: "Approved",
  for_record: "For Record Only",
  other: "Other",
};

/** @deprecated Use getClientDrawingWorkflowStatus for new UI; kept for Planning tab lists */
export function clientDrawingUiBucket(file) {
  const w = getClientDrawingWorkflowStatus(file);
  if (w === WORKFLOW_STATUS.NEEDS_REVIEW) return "needs_review";
  if (w === WORKFLOW_STATUS.UNDER_REVIEW) return "under_review";
  if (w === WORKFLOW_STATUS.REVISION_SUBMITTED) return "reviewed_waiting";
  if (w === WORKFLOW_STATUS.CHANGES_REQUESTED) return "changes_requested";
  if (w === WORKFLOW_STATUS.IN_PROGRESS) return "reviewed_waiting";
  if (w === WORKFLOW_STATUS.APPROVED) return "approved";
  const s = normStatus(file?.reviewStatus);
  if (s === "for_information" || s === "for_information_only" || s === "info_only") return "for_record";
  return "other";
}

export function groupDeliverablesByClientDrawingUi(files) {
  const buckets = {
    needs_review: [],
    under_review: [],
    changes_requested: [],
    reviewed_waiting: [],
    approved: [],
    for_record: [],
    other: [],
  };
  (Array.isArray(files) ? files : []).forEach((f) => {
    const k = clientDrawingUiBucket(f);
    if (buckets[k]) buckets[k].push(f);
    else buckets.other.push(f);
  });
  return buckets;
}
